import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';

import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { Alert, Box, Button, Tab, Tabs } from '@mui/material';

import Breadcrumbs from 'src/Lca/components/Breadcrumbs';
import { Subtitle, Title as PageTitle } from 'src/Lca/components/Texts';
import LcaLayout from 'src/Lca/components/layout';
import ProductInformation from './productInformation';
import { client } from 'src/utils/api-client';
import Goals from './goals';
import Boundaries from './boundaries';
import LcaInfo from './lca-info';
import Circularity from './circularity';
import {
  BigButton,
  BigButtonWhite,
  Desc as StepDesc,
  Title as StepTitle,
} from './index.styles';
import LifeCycle from './LifeCycle';
import _ from 'lodash';
import { useLcaOrganization } from 'src/common/hooks';
import { emptyProductForm, sanitizedValues } from '../utils';
import { StyledPanel } from 'src/Lca/styles';
import { useTranslation } from 'react-i18next';
import {
  DATA_COLLECTION_PERIOD_MAX_LENGTH,
  FUNCTIONAL_UNIT_MAX_LENGTH,
} from '../constants';
import { extractErrors } from 'src/utils/validation';
import {
  buildProductValidationErrors,
  ProductValidationErrors,
  ProductValidationErrorsProvider,
} from './validation';

export default function Steps() {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = useNavigate();
  const organization = useLcaOrganization();
  const queryClient = useQueryClient();
  const { t } = useTranslation('lca');

  type InitValueType = typeof emptyProductForm;

  const valuesWithPlaceholders = (values: InitValueType): InitValueType => {
    const newValues = _.cloneDeep(values);
    const atLeastOne: (keyof InitValueType)[] = [
      'nace_industries',
      'distribution_regions',
    ];
    for (const key of atLeastOne) {
      // copy missing elements from initial data
      const list = newValues[key] as any[];
      if (list?.length === 0) {
        list.push((emptyProductForm[key] as any[])[0]);
      }
    }
    const nullPlaceholder: (keyof InitValueType)[] = [
      'production_country',
      'cpc_industry',
    ];
    for (const key of nullPlaceholder) {
      (newValues[key] as any) ??= emptyProductForm[key];
    }

    for (const is_basic of [true, false]) {
      if (!newValues.materials.some((x) => x.is_basic === is_basic)) {
        newValues.materials.push({
          tempId: new Date().getTime(),
          material_name: '',
          percentage: 0,
          recycled_share_pct: 0,
          is_basic,
        });
      }
    }
    return newValues;
  };

  const [values, setValues] = useState(emptyProductForm);
  const [remoteValues, setRemoteValues] = useState<InitValueType | undefined>(
    undefined
  );
  if (!!params.id) {
    useQuery(
      'asd',
      () => {
        return client.get(`/web/lca/products/${params.id}`);
      },
      {
        onSuccess: ({ data }) => {
          setProductId(data.id);
          setValues(valuesWithPlaceholders(data));
          setRemoteValues(data);
        },
      }
    );
  }

  const data = [
    { name: t('productList'), url: '/lca/products' },
    { name: t('newProduct'), url: '' },
    { name: t('productStep1'), url: '' },
  ];

  const dataTabs = [
    { name: 'prod-information' },
    { name: 'circularity' },
    { name: 'goals' },
    { name: 'lca-information' },
    { name: 'boundaries' },
  ];

  const dataTabsFull = [
    { name: 'prod-information' },
    { name: 'circularity' },
    { name: 'goals' },
    { name: 'lca-information' },
    { name: 'boundaries' },
    { name: 'cycle' },
  ];

  const [error, setError] = useState<any>('');
  const [fieldErrors, setFieldErrors] = useState<ProductValidationErrors>({});
  const [productId, setProductId] = useState(0);

  const fullTabsAccess = !!productId && remoteValues?.lifecycle_boundaries;
  const saveFlowTabs = fullTabsAccess ? dataTabsFull : dataTabs;
  const tabs = saveFlowTabs;
  const tab = location.hash.split('#').pop() || 'prod-information';
  const tabsValue = tabs.some(({ name }) => name === tab) ? tab : false;
  const functionalUnitLength = values.functional_unit?.length || 0;
  const isFunctionalUnitTooLong =
    functionalUnitLength > FUNCTIONAL_UNIT_MAX_LENGTH;
  const dataCollectionPeriodLength = values.data_collection_period?.length || 0;
  const isDataCollectionPeriodTooLong =
    dataCollectionPeriodLength > DATA_COLLECTION_PERIOD_MAX_LENGTH;
  const dataCollectionLimitErrorMessage = t(
    'productCharacteristics.dataCollectionPeriodLimitError',
    { max: DATA_COLLECTION_PERIOD_MAX_LENGTH }
  );
  const functionalUnitLimitErrorMessage = t(
    'productCharacteristics.functionalUnitLimitError',
    { max: FUNCTIONAL_UNIT_MAX_LENGTH }
  );
  useEffect(() => {
    if (
      (!isDataCollectionPeriodTooLong &&
        error === dataCollectionLimitErrorMessage) ||
      (!isFunctionalUnitTooLong && error === functionalUnitLimitErrorMessage)
    ) {
      setError('');
    }
  }, [
    error,
    isDataCollectionPeriodTooLong,
    isFunctionalUnitTooLong,
    dataCollectionLimitErrorMessage,
    functionalUnitLimitErrorMessage,
  ]);

  const { mutate: mutatePut } = useMutation(
    () => {
      setError('');
      setFieldErrors({});
      return client.put(
        `/web/lca/products/${productId || params.id}`,
        sanitizedValues(values, organization)
      );
    },
    {
      onError: (e) => {
        const extractedErrors = extractErrors(e);
        const extractedErrorMessages = extractedErrors
          ? Object.values(extractedErrors).filter(
              (value): value is string =>
                typeof value === 'string' && value.length > 0
            )
          : [];
        const [firstExtractedError] = extractedErrorMessages;
        if (firstExtractedError) {
          setFieldErrors(
            buildProductValidationErrors(extractedErrors ?? {}, values)
          );
        }

        // @ts-ignore
        const detail = e?.response?.data?.detail;
        // @ts-ignore
        const message = e?.response?.data?.message;
        let errorMessage = message;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (firstExtractedError) {
          errorMessage = firstExtractedError;
        }
        setError(errorMessage);
      },
      onSuccess: ({ data }) => {
        setFieldErrors({});
        queryClient.invalidateQueries({
          queryKey: ['lca-diagram'],
        });
        const hasBoundry = !!data?.lifecycle_boundaries;
        const _tabs = hasBoundry ? dataTabsFull : dataTabs;
        const currTab = _tabs.findIndex((t) => t.name === tab);
        const newTab = _tabs[currTab + 1]?.name;
        if (newTab) {
          navigate(`#${newTab}`);
          setTimeout(() => window.scrollTo(0, 0), 200);
        }
        setRemoteValues(data);
      },
    }
  );

  /* TODO: ogarnąć nawigację */

  if (!params.id || !parseInt(params.id, 10))
    return <Navigate to="/404" replace />;

  const handleSave = () => {
    setFieldErrors({});
    if (isFunctionalUnitTooLong) {
      setError(functionalUnitLimitErrorMessage);
      window.scrollTo(0, 0);
      return;
    }
    if (isDataCollectionPeriodTooLong) {
      setError(dataCollectionLimitErrorMessage);
      window.scrollTo(0, 0);
      return;
    }
    mutatePut();
  };

  return (
    <LcaLayout>
      <Breadcrumbs data={data} />
      <Subtitle text={t('product.productAndLcaInformation')} />
      <div style={{ marginTop: '44px', marginBottom: '40px' }}>
        <PageTitle text={!productId ? t('newProduct') : values.name} />
      </div>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: '16px',
        }}
      >
        <Tabs
          value={tabsValue}
          onChange={(_, value) => navigate(`#${value}`)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            flex: '1 1 640px',
            minWidth: 0,
            maxWidth: '100%',
          }}
        >
          {tabs.map((x: any) => (
            <Tab key={x.name} label={t(`tabs.${x.name}`)} value={x.name} />
          ))}
        </Tabs>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '16px',
            justifyContent: 'flex-end',
            marginLeft: 'auto',
            maxWidth: '100%',
          }}
        >
          {!!productId && (
            <>
              <Button
                size="small"
                variant="secondary"
                startIcon={<HelpOutlineOutlinedIcon />}
                onClick={() => navigate('/lca/help-centre')}
              >
                {t('knowledgeBase')}
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() =>
                  navigate(`/lca/products/${productId}/report-builder`)
                }
              >
                {t('product.downloadReport')}
              </Button>
              <Button
                size="small"
                variant="secondary"
                onClick={() =>
                  navigate(`/lca/products/${productId}/share-product-passport`)
                }
              >
                {t('product.sharePassport')}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!!error && <Alert severity="error">{error}</Alert>}

      <ProductValidationErrorsProvider errors={fieldErrors}>
        {tab === 'prod-information' && (
          <StyledPanel>
            <ProductInformation values={values} setValues={setValues} />
          </StyledPanel>
        )}
        {tab === 'goals' && (
          <StyledPanel>
            <Goals values={values} setValues={setValues} />
          </StyledPanel>
        )}
        {tab === 'circularity' && (
          <StyledPanel>
            <Circularity values={values} setValues={setValues} />
          </StyledPanel>
        )}
        {tab === 'lca-information' && (
          <StyledPanel>
            <LcaInfo values={values} setValues={setValues} />
          </StyledPanel>
        )}
        {tab === 'boundaries' && (
          <StyledPanel>
            <Boundaries values={values} setValues={setValues} />
          </StyledPanel>
        )}
      </ProductValidationErrorsProvider>

      {tab === 'cycle' && (
        <div style={{ marginBottom: 200 }}>
          <StepTitle>{t('lifeCycles.title')}</StepTitle>
          <StepDesc>{t('lifeCycles.desc')}</StepDesc>
          <LifeCycle productId={productId} />
        </div>
      )}

      {tab !== 'cycle' && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 40,
            marginBottom: 80,
          }}
        >
          <BigButtonWhite onClick={() => nav(-1)} type="button">
            {t('product.back')}
          </BigButtonWhite>
          <BigButton onClick={handleSave} type="button">
            {t('product.saveAndContinue')}
          </BigButton>
        </div>
      )}
    </LcaLayout>
  );
}
